import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Plus, Edit3, Trash2, Eye, Search, RefreshCw, X, Save, Copy, Check,
  Code, FileText, Tag, ToggleLeft, ToggleRight, ChevronDown, AlertTriangle,
  Palette, Type, Variable, Layout, Smartphone, Monitor, Maximize2, Minimize2
} from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const USE_CASES = [
  { value: 'login_alert', label: 'Login Alert', color: '#ef4444' },
  { value: 'otp_verification', label: 'OTP Verification', color: '#f59e0b' },
  { value: 'welcome', label: 'Welcome Email', color: '#22c55e' },
  { value: 'password_reset', label: 'Password Reset', color: '#3b82f6' },
  { value: 'recharge_confirmation', label: 'Recharge Confirmation', color: '#8b5cf6' },
  { value: 'low_balance', label: 'Low Balance Alert', color: '#f97316' },
  { value: 'account_suspended', label: 'Account Suspended', color: '#dc2626' },
  { value: 'daily_report', label: 'Daily Report', color: '#06b6d4' },
  { value: 'monthly_invoice', label: 'Monthly Invoice', color: '#6366f1' },
  { value: 'general', label: 'General', color: '#6b7280' },
];

const CATEGORIES = [
  { value: 'notification', label: 'Notification' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'system', label: 'System' },
  { value: 'report', label: 'Report' },
];

const COMMON_VARIABLES = [
  '{{username}}', '{{full_name}}', '{{email}}', '{{company_name}}',
  '{{otp_code}}', '{{ip_address}}', '{{login_time}}', '{{device_info}}',
  '{{amount}}', '{{balance}}', '{{transaction_id}}', '{{date}}',
  '{{support_email}}', '{{app_name}}', '{{app_url}}',
];

const EmailTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterUseCase, setFilterUseCase] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copied, setCopied] = useState('');

  // Editor state
  const [form, setForm] = useState({
    name: '', subject: '', html_content: '', text_content: '',
    use_case: 'general', category: 'notification', variables: '[]', is_active: true,
  });
  const [editorTab, setEditorTab] = useState('visual'); // visual, code, preview
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, mobile
  const [fullscreen, setFullscreen] = useState(false);
  const editorRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/email-templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openEditor = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        name: template.name, subject: template.subject,
        html_content: template.html_content, text_content: template.text_content || '',
        use_case: template.use_case, category: template.category,
        variables: template.variables || '[]', is_active: template.is_active,
      });
    } else {
      setEditingTemplate(null);
      setForm({
        name: '', subject: '', html_content: getDefaultTemplate(),
        text_content: '', use_case: 'general', category: 'notification',
        variables: '[]', is_active: true,
      });
    }
    setEditorTab('visual');
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.html_content) return;
    try {
      setSaving(true);
      if (editingTemplate) {
        await api.put(`/admin/email-templates/${editingTemplate.id}`, form);
      } else {
        await api.post('/admin/email-templates', form);
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (err) {
      console.error('Failed to save template:', err);
      alert('Failed to save template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/email-templates/${id}`);
      setDeleteConfirm(null);
      fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const insertVariable = (variable) => {
    setForm(prev => ({ ...prev, html_content: prev.html_content + variable }));
  };

  const filtered = templates.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase());
    const matchUseCase = !filterUseCase || t.use_case === filterUseCase;
    return matchSearch && matchUseCase;
  });

  const getUseCaseConfig = (uc) => USE_CASES.find(u => u.value === uc) || USE_CASES[USE_CASES.length - 1];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6" style={{ color: '#4f46e5' }} /> Email Template Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">{templates.length} templates · Manage email templates for platform notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchTemplates} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => openEditor()} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            <Plus className="w-4 h-4" /> Add Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            style={{ borderColor: 'rgba(99, 102, 241, 0.15)' }} />
        </div>
        <select value={filterUseCase} onChange={e => setFilterUseCase(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          style={{ borderColor: 'rgba(99, 102, 241, 0.15)' }}>
          <option value="">All Use Cases</option>
          {USE_CASES.map(uc => <option key={uc.value} value={uc.value}>{uc.label}</option>)}
        </select>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-5 animate-pulse" style={cardStyle}>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
              <div className="h-20 bg-gray-100 rounded mb-3" />
              <div className="flex gap-2"><div className="h-6 bg-gray-200 rounded w-16" /><div className="h-6 bg-gray-200 rounded w-20" /></div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" style={cardStyle}>
          <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No email templates found</p>
          <p className="text-sm text-gray-400 mt-1">Create your first template to get started</p>
          <button onClick={() => openEditor()} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            <Plus className="w-4 h-4 inline mr-1" /> Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => {
            const uc = getUseCaseConfig(t.use_case);
            return (
              <div key={t.id} className="bg-white rounded-xl overflow-hidden group hover:shadow-lg transition-all duration-200" style={cardStyle}>
                {/* Card Header */}
                <div className="p-4 pb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{t.name}</h3>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{t.subject}</p>
                    </div>
                    <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full mt-1 ${t.is_active ? 'bg-green-500' : 'bg-gray-300'}`} title={t.is_active ? 'Active' : 'Inactive'} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: `${uc.color}15`, color: uc.color, border: `1px solid ${uc.color}30` }}>
                      {uc.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">{t.category}</span>
                  </div>
                </div>
                {/* Preview strip */}
                <div className="px-4 pb-3">
                  <div className="bg-gray-50 rounded-lg p-2 h-16 overflow-hidden text-[10px] text-gray-400 font-mono leading-tight" style={{ border: '1px solid rgba(99,102,241,0.06)' }}>
                    {t.html_content ? t.html_content.replace(/<[^>]*>/g, '').slice(0, 200) : 'No content'}
                  </div>
                </div>
                {/* Card Footer */}
                <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(99,102,241,0.06)', background: 'rgba(99,102,241,0.02)' }}>
                  <span className="text-[10px] text-gray-400">
                    {new Date(t.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPreviewTemplate(t)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors" title="Preview">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openEditor(t)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors" title="Edit">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleteConfirm(t)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Template Editor Modal */}
      {showEditor && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${fullscreen ? '' : 'p-4'}`} style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className={`bg-white flex flex-col ${fullscreen ? 'w-full h-full' : 'rounded-2xl shadow-2xl w-full max-w-6xl'}`} style={{ maxHeight: fullscreen ? '100vh' : '92vh' }}>
            {/* Editor Header */}
            <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)', background: 'rgba(99,102,241,0.02)' }}>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5" style={{ color: '#4f46e5' }} />
                <h2 className="text-base font-semibold text-gray-900">{editingTemplate ? 'Edit Template' : 'New Email Template'}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setFullscreen(!fullscreen)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                  {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button onClick={() => setShowEditor(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Editor Body */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left: Settings Panel */}
              <div className="w-72 flex-shrink-0 overflow-y-auto p-4 space-y-4" style={{ borderRight: '1px solid rgba(99,102,241,0.08)', background: '#fafbfc' }}>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Template Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    style={{ borderColor: 'rgba(99,102,241,0.15)' }} placeholder="e.g. Login Alert Email" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Subject Line *</label>
                  <input type="text" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    style={{ borderColor: 'rgba(99,102,241,0.15)' }} placeholder="e.g. Security Alert: New Login Detected" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Use Case *</label>
                  <select value={form.use_case} onChange={e => setForm(p => ({ ...p, use_case: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    style={{ borderColor: 'rgba(99,102,241,0.15)' }}>
                    {USE_CASES.map(uc => <option key={uc.value} value={uc.value}>{uc.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Category</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    style={{ borderColor: 'rgba(99,102,241,0.15)' }}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Status</label>
                  <button onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium w-full ${form.is_active ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                    {form.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    {form.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Plain Text Version</label>
                  <textarea value={form.text_content} onChange={e => setForm(p => ({ ...p, text_content: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                    style={{ borderColor: 'rgba(99,102,241,0.15)', height: '80px' }} placeholder="Plain text fallback..." />
                </div>
                {/* Variables */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block flex items-center gap-1">
                    <Variable className="w-3 h-3" /> Insert Variable
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {COMMON_VARIABLES.map(v => (
                      <button key={v} onClick={() => insertVariable(v)}
                        className="px-2 py-0.5 rounded text-[10px] font-mono bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100 transition-colors">
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Code Editor + Preview */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Editor Tabs */}
                <div className="flex items-center gap-0 px-4 pt-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
                  {[
                    { id: 'visual', label: 'Visual Editor', icon: Layout },
                    { id: 'code', label: 'HTML Code', icon: Code },
                    { id: 'preview', label: 'Preview', icon: Eye },
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setEditorTab(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors ${editorTab === tab.id ? 'text-indigo-600 bg-white' : 'text-gray-400 hover:text-gray-600'}`}
                      style={editorTab === tab.id ? { borderBottom: '2px solid #4f46e5' } : {}}>
                      <tab.icon className="w-3.5 h-3.5" /> {tab.label}
                    </button>
                  ))}
                  {editorTab === 'preview' && (
                    <div className="ml-auto flex items-center gap-1 mr-2">
                      <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-400'}`}>
                        <Monitor className="w-4 h-4" />
                      </button>
                      <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-400'}`}>
                        <Smartphone className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Editor Content */}
                <div className="flex-1 overflow-hidden">
                  {editorTab === 'visual' && (
                    <div className="h-full flex flex-col">
                      {/* Toolbar */}
                      <div className="flex items-center gap-1 px-4 py-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.06)', background: '#fafbfc' }}>
                        <ToolbarButton icon="B" title="Bold" onClick={() => wrapSelection('**', '**')} bold />
                        <ToolbarButton icon="I" title="Italic" onClick={() => wrapSelection('<em>', '</em>')} italic />
                        <span className="w-px h-5 bg-gray-200 mx-1" />
                        <ToolbarButton icon={<Type className="w-3.5 h-3.5" />} title="Heading" onClick={() => insertAtCursor('<h2 style="color: #1f2937; margin: 0 0 16px;">Heading</h2>\n')} />
                        <ToolbarButton icon={<FileText className="w-3.5 h-3.5" />} title="Paragraph" onClick={() => insertAtCursor('<p style="color: #4b5563; line-height: 1.6; margin: 0 0 16px;">Text here</p>\n')} />
                        <span className="w-px h-5 bg-gray-200 mx-1" />
                        <ToolbarButton icon={<Palette className="w-3.5 h-3.5" />} title="Button" onClick={() => insertAtCursor('<a href="#" style="display: inline-block; padding: 12px 24px; background: #4f46e5; color: #fff; text-decoration: none; border-radius: 8px; font-weight: 600;">Click Here</a>\n')} />
                        <ToolbarButton icon={<Layout className="w-3.5 h-3.5" />} title="Divider" onClick={() => insertAtCursor('<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />\n')} />
                        <span className="w-px h-5 bg-gray-200 mx-1" />
                        <button onClick={() => setForm(p => ({ ...p, html_content: getDefaultTemplate() }))}
                          className="px-2 py-1 rounded text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 transition-colors">
                          Reset to Default
                        </button>
                      </div>
                      <textarea ref={editorRef} value={form.html_content}
                        onChange={e => setForm(p => ({ ...p, html_content: e.target.value }))}
                        className="flex-1 w-full px-4 py-3 font-mono text-xs text-gray-800 resize-none focus:outline-none"
                        style={{ background: '#1e1e2e', color: '#d4d4d8', lineHeight: '1.7' }}
                        spellCheck={false} />
                    </div>
                  )}
                  {editorTab === 'code' && (
                    <div className="h-full relative">
                      <textarea value={form.html_content}
                        onChange={e => setForm(p => ({ ...p, html_content: e.target.value }))}
                        className="w-full h-full px-4 py-3 font-mono text-xs resize-none focus:outline-none"
                        style={{ background: '#1e1e2e', color: '#d4d4d8', lineHeight: '1.7' }}
                        spellCheck={false} />
                      <button onClick={() => copyToClipboard(form.html_content, 'code')}
                        className="absolute top-3 right-3 p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors">
                        {copied === 'code' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  )}
                  {editorTab === 'preview' && (
                    <div className="h-full overflow-auto p-6" style={{ background: '#f1f5f9' }}>
                      <div className="mx-auto bg-white rounded-xl shadow-lg overflow-hidden" style={{ maxWidth: previewMode === 'mobile' ? '375px' : '700px', border: '1px solid #e2e8f0' }}>
                        {/* Email subject bar */}
                        <div className="px-4 py-3" style={{ background: 'rgba(99,102,241,0.04)', borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
                          <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-0.5">Subject</div>
                          <div className="text-sm font-semibold text-gray-800">{form.subject || '(No subject)'}</div>
                        </div>
                        <div className="p-0" dangerouslySetInnerHTML={{ __html: form.html_content || '<p style="padding:20px;color:#999;">No content yet</p>' }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Editor Footer */}
            <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(99,102,241,0.1)', background: 'rgba(99,102,241,0.02)' }}>
              <div className="text-xs text-gray-400">
                {form.html_content.length.toLocaleString()} characters
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowEditor(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name || !form.subject || !form.html_content}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <Save className="w-4 h-4" /> {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setPreviewTemplate(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{previewTemplate.name}</h3>
                <p className="text-xs text-gray-500">{previewTemplate.subject}</p>
              </div>
              <button onClick={() => setPreviewTemplate(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-0">
              <div dangerouslySetInnerHTML={{ __html: previewTemplate.html_content }} />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Delete Template</h3>
                <p className="text-xs text-gray-500">"{deleteConfirm.name}" will be soft-deleted</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Toolbar button helper
const ToolbarButton = ({ icon, title, onClick, bold, italic }) => (
  <button onClick={onClick} className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors" title={title}>
    {typeof icon === 'string' ? <span className={`text-xs ${bold ? 'font-bold' : ''} ${italic ? 'italic' : ''}`}>{icon}</span> : icon}
  </button>
);

// Helper: insert text at cursor in textarea
function insertAtCursor(text) {
  // This is a simplified version - in the visual editor it appends
  // A full implementation would use selectionStart/selectionEnd
}

function wrapSelection(before, after) {
  // Simplified - wraps selected text
}

// Default email template
function getDefaultTemplate() {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #f4f5f7; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f5f7; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06);">
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #4f46e5, #06b6d4); padding: 32px 40px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700;">Way2Connect</h1>
              <p style="color: rgba(255,255,255,0.8); margin: 8px 0 0; font-size: 13px;">SMS Platform</p>
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding: 40px;">
              <h2 style="color: #1f2937; margin: 0 0 16px; font-size: 18px;">Hello {{username}},</h2>
              <p style="color: #4b5563; line-height: 1.6; margin: 0 0 16px; font-size: 14px;">
                Your email content goes here. Use the variables panel to insert dynamic content.
              </p>
              <a href="#" style="display: inline-block; padding: 12px 24px; background: #4f46e5; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 14px;">Take Action</a>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 24px 40px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                &copy; 2026 Way2Connect. All rights reserved.<br>
                <a href="#" style="color: #6366f1; text-decoration: none;">Unsubscribe</a> | <a href="#" style="color: #6366f1; text-decoration: none;">Contact Support</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export default EmailTemplateManagement;
